import axios from "axios";
import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";

const api = new APIClient();

const RESOURCE = "/financial/day-close";

export type DayClosePaymentMethod =
  | "cash"
  | "pix_external"
  | "debit_card_terminal"
  | "credit_card_terminal"
  | "pix_gateway"
  | "card_terminal_gateway";

export interface DayCloseMethodBreakdown {
  payment_method: DayClosePaymentMethod | string;
  gateway_slug: string | null;
  amount_cents: number;
  fee_cents: number;
  net_cents: number;
  payments_count: number;
  orders_count: number;
}

export interface DayCloseTotals {
  gross_cents: number;
  fee_cents: number;
  net_cents: number;
  payments_count: number;
  orders_count: number;
}

export interface DayCloseComparisonYesterday extends DayCloseTotals {
  date: string;
}

export interface DayCloseComparisonWeekAvg extends DayCloseTotals {
  days: number;
}

export interface DayCloseAnomaly {
  flagged: boolean;
  direction: "up" | "down" | null;
  threshold_percent: number;
  delta_vs_week_avg_cents: number;
  week_avg_net_cents: number;
}

export interface DayCloseUnitBreakdown {
  unit_id: number;
  totals: DayCloseTotals;
}

export interface DayCloseTrendPoint {
  date: string;
  gross_cents: number;
  net_cents: number;
}

export interface DayCloseReviewer {
  id: number;
  name: string;
}

export interface DayCloseCommissionsSummary {
  enabled: boolean;
  total_cents: number;
  lines_count: number;
  lines_sum_cents: number;
  totals_match: boolean;
}

export interface DayCloseReport {
  unit_id: number | null;
  date: string;
  timezone: string;
  cash_register_mode: "unified" | "per_professional" | string;
  currency: string;
  professional_id: number | null;
  totals: DayCloseTotals;
  by_method: DayCloseMethodBreakdown[];
  /** Optional commission conference card (ADJ-F2-123). */
  commissions?: DayCloseCommissionsSummary;
  comparison: {
    yesterday: DayCloseComparisonYesterday;
    week_avg: DayCloseComparisonWeekAvg;
  };
  anomaly?: DayCloseAnomaly;
  trend?: DayCloseTrendPoint[];
  consolidated?: boolean;
  units?: DayCloseUnitBreakdown[];
  reviewed?: boolean;
  reviewed_at?: string | null;
  reviewed_by?: DayCloseReviewer | null;
  cash_counted_cents?: number | null;
  cash_system_cents?: number;
  cash_difference_cents?: number | null;
  day_fully_reviewed?: boolean;
  reminder_hour?: number;
}

export interface DayCloseReportParams {
  date: string;
  unit_id?: number;
  consolidate?: boolean;
  professional_id?: number;
}

export interface DayCloseReviewPayload {
  date: string;
  unit_id?: number;
  consolidate?: boolean;
  cash_counted_cents?: number | null;
}

export interface DayCloseReviewResult {
  reviewed: boolean;
  reviewed_at: string | null;
  reviewed_by: DayCloseReviewer | null;
  unit_id: number | null;
  cash_counted_cents: number | null;
  cash_system_cents: number;
  cash_difference_cents: number | null;
}

const buildQuery = (params: DayCloseReportParams): string => {
  const search = new URLSearchParams({
    date: params.date,
  });

  if (params.consolidate) {
    search.set("consolidate", "1");
  } else if (params.unit_id) {
    search.set("unit_id", String(params.unit_id));
  }

  if (params.professional_id) {
    search.set("professional_id", String(params.professional_id));
  }

  return search.toString();
};

export const getDayCloseReport = (
  params: DayCloseReportParams
): Promise<ApiResponse<DayCloseReport>> =>
  api.get<ApiResponse<DayCloseReport>>(RESOURCE, {
    date: params.date,
    ...(params.consolidate
      ? { consolidate: true }
      : { unit_id: params.unit_id }),
    ...(params.professional_id
      ? { professional_id: params.professional_id }
      : {}),
  });

export const markDayCloseReviewed = (
  payload: DayCloseReviewPayload
): Promise<ApiResponse<DayCloseReviewResult>> =>
  api.create<ApiResponse<DayCloseReviewResult>>(`${RESOURCE}/review`, payload);

export const clearDayCloseReviewed = (
  payload: DayCloseReviewPayload
): Promise<ApiResponse<DayCloseReviewResult>> => {
  const search = new URLSearchParams({ date: payload.date });
  if (payload.consolidate) {
    search.set("consolidate", "1");
  } else if (payload.unit_id) {
    search.set("unit_id", String(payload.unit_id));
  }

  return api.delete<ApiResponse<DayCloseReviewResult>>(
    `${RESOURCE}/review?${search.toString()}`
  );
};

export const downloadDayCloseCsv = async (
  params: DayCloseReportParams
): Promise<Blob> => {
  const blob = await axios.get<Blob>(`${RESOURCE}/csv?${buildQuery(params)}`, {
    responseType: "blob",
  });

  return blob as unknown as Blob;
};

export const downloadDayClosePdf = async (
  params: DayCloseReportParams
): Promise<Blob> => {
  const blob = await axios.get<Blob>(`${RESOURCE}/pdf?${buildQuery(params)}`, {
    responseType: "blob",
  });

  return blob as unknown as Blob;
};

export const triggerBrowserDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

export const buildDayClosePath = (params: {
  unitId?: number;
  date: string;
  professionalId?: number;
  consolidate?: boolean;
}): string => {
  const search = new URLSearchParams({
    date: params.date,
  });

  if (params.consolidate) {
    search.set("consolidate", "1");
  } else if (params.unitId) {
    search.set("unit_id", String(params.unitId));
  }

  if (params.professionalId) {
    search.set("professional_id", String(params.professionalId));
  }

  return `/financial/day-close?${search.toString()}`;
};

export const cashSystemCentsFromReport = (report: DayCloseReport): number =>
  report.by_method
    .filter((line) => line.payment_method === "cash")
    .reduce((sum, line) => sum + line.net_cents, 0);
